import React, { useState, useEffect } from "react";
import "./business225.css";
import Navbar from '../../Navbar/nbar';
import Footer from '../../footer/Footer';

const fmt = n => '₹' + Math.round(n).toLocaleString('en-IN');
const fmtL = n => { if (n >= 1e7) return '₹' + (n/1e7).toFixed(1) + ' Cr'; if (n >= 1e5) return '₹' + (n/1e5).toFixed(1) + ' L'; return fmt(n); };

/* ═══════════════════════════════════════
   ROI CALCULATOR
   ═══════════════════════════════════════ */
function ROICalculator() {
  const [revenue, setRevenue] = useState(5000000);
  const [employees, setEmployees] = useState(25);
  const [manualHours, setManualHours] = useState(40);
  const [errorRate, setErrorRate] = useState(5);

  const hourlyRate = revenue > 0 ? (revenue / 12) / (employees * 160) : 0;
  const timeSaved = manualHours * 0.65;
  const monthlySavings = timeSaved * hourlyRate * 4.33 * employees * 0.3;
  const errorReduction = (errorRate * 0.7);
  const errorSavings = (revenue * errorRate / 100) * 0.7 / 12;
  const totalMonthlySavings = monthlySavings + errorSavings;
  const annualSavings = totalMonthlySavings * 12;
  const roi = revenue > 0 ? ((annualSavings / (revenue * 0.02)) * 100) : 0;

  const SR = ({ label, value, onChange, max, suffix }) => (
    <div className="biz__sr">
      <div className="biz__sr-head"><span>{label}</span><span className="biz__sr-val">{suffix === '₹' ? fmtL(value) : `${value}${suffix || ''}`}</span></div>
      <input type="range" className="biz__slider" min={suffix === '₹' ? 500000 : 1} max={max} step={suffix === '₹' ? 500000 : 1} value={value} onChange={e => onChange(+e.target.value)} />
    </div>
  );

  return (
    <section className="biz__section biz__section--roi" id="roi-calculator">
      <div className="biz__inner">
        <div className="biz__section-header">
          <span className="biz__tag">ROI Calculator</span>
          <div className="biz__section-title">See What SOMFIN Saves Your Business</div>
          <p className="biz__section-sub">Enter your business metrics to see projected savings from automating financial operations.</p>
        </div>
        <div className="biz__roi-grid">
          <div className="biz__roi-left">
            <div className="biz__tcard">
              <div className="biz__tcard-title">Your Business</div>
              <SR label="Annual Revenue" value={revenue} onChange={setRevenue} max={100000000} suffix="₹" />
              <SR label="Employees in Finance/Ops" value={employees} onChange={setEmployees} max={200} suffix="" />
            </div>
            <div className="biz__tcard">
              <div className="biz__tcard-title">Current Pain Points</div>
              <SR label="Manual Hours / Week" value={manualHours} onChange={setManualHours} max={120} suffix=" hrs" />
              <SR label="Error Rate (%)" value={errorRate} onChange={setErrorRate} max={20} suffix="%" />
            </div>
          </div>
          <div className="biz__roi-right">
            <div className="biz__tcard biz__tcard--highlight">
              <div className="biz__tcard-title">Projected Impact</div>
              <div className="biz__roi-metrics">
                <div className="biz__roi-metric">
                  <div className="biz__roi-metric-val biz__green">{fmtL(annualSavings)}</div>
                  <div className="biz__roi-metric-label">Annual Savings</div>
                </div>
                <div className="biz__roi-metric">
                  <div className="biz__roi-metric-val">{Math.round(timeSaved)} hrs</div>
                  <div className="biz__roi-metric-label">Weekly Time Saved</div>
                </div>
                <div className="biz__roi-metric">
                  <div className="biz__roi-metric-val biz__green">{errorReduction.toFixed(1)}%</div>
                  <div className="biz__roi-metric-label">Error Reduction</div>
                </div>
                <div className="biz__roi-metric">
                  <div className="biz__roi-metric-val" style={{ color: roi > 200 ? '#1A8A63' : '#4f297e' }}>{Math.round(roi)}%</div>
                  <div className="biz__roi-metric-label">Estimated ROI</div>
                </div>
              </div>
            </div>
            <div className="biz__tcard">
              <div className="biz__tcard-title">Monthly Breakdown</div>
              <div className="biz__roi-bar">
                <div className="biz__roi-bar-seg biz__roi-bar-seg--time" style={{ width: `${totalMonthlySavings > 0 ? (monthlySavings / totalMonthlySavings * 100) : 50}%` }}>
                  {totalMonthlySavings > 0 && <span>Time</span>}
                </div>
                <div className="biz__roi-bar-seg biz__roi-bar-seg--error" style={{ width: `${totalMonthlySavings > 0 ? (errorSavings / totalMonthlySavings * 100) : 50}%` }}>
                  {totalMonthlySavings > 0 && <span>Errors</span>}
                </div>
              </div>
              <div className="biz__roi-breakdown">
                <div><i style={{ background: '#4f297e' }} /><span>Time saved</span><strong>{fmtL(monthlySavings)}/mo</strong></div>
                <div><i style={{ background: '#1A8A63' }} /><span>Error reduction</span><strong>{fmtL(errorSavings)}/mo</strong></div>
                <div className="biz__roi-total"><span>Total monthly savings</span><strong>{fmtL(totalMonthlySavings)}</strong></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════ */
const FinanceCore = () => {
  const [activeTab, setActiveTab] = useState('analytics');
  const [activeSlide, setActiveSlide] = useState(0);

  const challenges = [
    { quote: "Managing finances alongside daily operations has become an energy-draining task for a business owner like me.", author: "Keshav R", role: "MSME Owner, Chennai" },
    { quote: "Missing ongoing market updates occasionally led to operational cost spikes of up to 40%.", author: "Prasanth D Subramaniyan", role: "Manager, Vinayaka Enterprise" },
    { quote: "Unpredictable errors can negatively affect operations and significantly raise the overall cost of running the business.", author: "Ishika Bhandarkar", role: "Business Owner, Pune" },
  ];

  useEffect(() => {
    const timer = setInterval(() => setActiveSlide(p => (p + 1) % challenges.length), 6000);
    return () => clearInterval(timer);
  }, [challenges.length]);

  const Icon = ({ type }) => {
    const paths = {
      chart: <><rect x="3" y="12" width="4" height="9" rx="1" /><rect x="10" y="7" width="4" height="14" rx="1" /><rect x="17" y="3" width="4" height="18" rx="1" /></>,
      target: <><circle cx="12" cy="12" r="9" fill="none" strokeWidth="2" /><circle cx="12" cy="12" r="5" fill="none" strokeWidth="2" /><circle cx="12" cy="12" r="1.5" /></>,
      trend: <><polyline points="3 17 9 11 13 15 21 7" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /><polyline points="17 7 21 7 21 11" fill="none" strokeWidth="2" strokeLinecap="round" /></>,
      scale: <><path d="M12 3v18M3 7l9-4 9 4" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /><path d="M3 7l3 7h-6l3-7M21 7l3 7h-6l3-7" fill="none" strokeWidth="1.5" /></>,
      search: <><circle cx="10" cy="10" r="7" fill="none" strokeWidth="2" /><line x1="15" y1="15" x2="21" y2="21" strokeWidth="2" strokeLinecap="round" /></>,
      clipboard: <><rect x="5" y="3" width="14" height="18" rx="2" fill="none" strokeWidth="2" /><line x1="9" y1="9" x2="15" y2="9" strokeWidth="1.5" strokeLinecap="round" /><line x1="9" y1="13" x2="15" y2="13" strokeWidth="1.5" strokeLinecap="round" /><line x1="9" y1="17" x2="12" y2="17" strokeWidth="1.5" strokeLinecap="round" /></>,
      plug: <><path d="M12 2v6M8 2v6M16 2v6" fill="none" strokeWidth="2" strokeLinecap="round" /><rect x="6" y="8" width="12" height="6" rx="2" fill="none" strokeWidth="2" /><path d="M12 14v4M8 18h8" fill="none" strokeWidth="2" strokeLinecap="round" /></>,
      globe: <><circle cx="12" cy="12" r="9" fill="none" strokeWidth="2" /><ellipse cx="12" cy="12" rx="4" ry="9" fill="none" strokeWidth="1.5" /><line x1="3" y1="12" x2="21" y2="12" strokeWidth="1.5" /></>,
      bolt: <><polygon points="13 2 3 14 12 14 11 22 21 10 12 10" fill="none" strokeWidth="2" strokeLinejoin="round" /></>,
      lock: <><rect x="5" y="11" width="14" height="10" rx="2" fill="none" strokeWidth="2" /><path d="M8 11V7a4 4 0 018 0v4" fill="none" strokeWidth="2" /><circle cx="12" cy="16" r="1.5" /></>,
      shield: <><path d="M12 3L4 7v5c0 5 3.5 9.7 8 11 4.5-1.3 8-6 8-11V7l-8-4z" fill="none" strokeWidth="2" /><polyline points="9 12 11 14 15 10" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></>,
      alert: <><path d="M12 2L2 20h20L12 2z" fill="none" strokeWidth="2" strokeLinejoin="round" /><line x1="12" y1="9" x2="12" y2="13" strokeWidth="2" strokeLinecap="round" /><circle cx="12" cy="17" r="1" /></>,
    };
    return (
      <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor" stroke="currentColor" className="biz__icon-svg">
        {paths[type]}
      </svg>
    );
  };

  const tabs = {
    analytics: [
      { icon: 'chart', title: 'Real-Time Analytics', desc: 'Live dashboards with multi-dimensional financial analysis. Track cash flow, receivables, and burn rate across entities in real time.' },
      { icon: 'target', title: 'Risk Assessment', desc: 'Scenario modeling and stress testing across market, credit, and operational risk vectors. Monte Carlo simulations built in.' },
      { icon: 'trend', title: 'Forecasting Engine', desc: 'Machine learning forecasts for revenue, expenses, and working capital. Adjusts for seasonality and market conditions automatically.' },
    ],
    compliance: [
      { icon: 'scale', title: 'Regulatory Reporting', desc: 'Automated GST, TDS, and RBI compliance reports. Generate filings in minutes instead of days with pre-built templates.' },
      { icon: 'search', title: 'AML Monitoring', desc: 'Transaction monitoring and suspicious activity detection. Configurable rule engine with ML-powered anomaly scoring.' },
      { icon: 'clipboard', title: 'Audit Trail', desc: 'Immutable, timestamped logs of every financial operation. Full traceability for internal audits and regulatory examinations.' },
    ],
    integration: [
      { icon: 'plug', title: 'API Gateway', desc: 'RESTful and webhook APIs with OAuth 2.0. Connect to your ERP, accounting software, banking channels, and payment gateways.' },
      { icon: 'globe', title: 'Multi-Platform', desc: 'Cloud-native deployment with Tally, Zoho Books, QuickBooks, and SAP integrations. On-premise options for regulated entities.' },
      { icon: 'bolt', title: 'Real-Time Sync', desc: 'Sub-second data synchronisation across all connected systems. No batch processing delays — every number is current.' },
    ],
    security: [
      { icon: 'lock', title: 'Zero Trust Architecture', desc: 'Multi-factor authentication, role-based access, and device fingerprinting. Every request is verified, never trusted by default.' },
      { icon: 'shield', title: 'Encryption', desc: 'AES-256 encryption at rest and in transit. Dedicated key management with HSM support. SOC 2 Type II certified infrastructure.' },
      { icon: 'alert', title: 'Threat Detection', desc: '24/7 security monitoring with AI-powered anomaly detection. Automated incident response and real-time alerting.' },
    ],
  };

  const products = [
    { title: 'Financial Command Centre', desc: 'Unified dashboard for all financial operations — cash flow, payables, receivables, and treasury — across multiple entities and accounts.', features: ['Multi-entity consolidated view', 'Automated bank reconciliation', 'Cash flow forecasting', 'Custom alert thresholds', 'Role-based access control'] },
    { title: 'Compliance & Reporting Suite', desc: 'Stay ahead of regulatory requirements with automated filing, real-time monitoring, and pre-built templates for GST, TDS, and RBI reporting.', features: ['One-click GST filing', 'TDS computation engine', 'Audit-ready documentation', 'Regulatory change alerts', 'Custom report builder'] },
    { title: 'Risk Intelligence Platform', desc: 'Identify, measure, and mitigate financial risks across your business with scenario modeling, stress testing, and continuous monitoring.', features: ['Market risk dashboards', 'Credit risk scoring', 'Operational risk matrix', 'What-if scenario engine', 'Early warning indicators'] },
  ];

  return (
    <div className="biz__page">
      <Navbar />

      {/* HERO */}
      <section className="biz__hero">
        <svg className="biz__hero-art" viewBox="0 0 800 400" preserveAspectRatio="xMidYMid slice">
          <circle cx="100" cy="120" r="90" fill="none" stroke="rgba(79,41,126,0.06)" strokeWidth="1" />
          <circle cx="700" cy="100" r="120" fill="none" stroke="rgba(26,138,99,0.04)" strokeWidth="1" />
          <circle cx="450" cy="350" r="65" fill="none" stroke="rgba(79,41,126,0.05)" strokeWidth="1" />
          <line x1="190" y1="120" x2="385" y2="350" stroke="rgba(79,41,126,0.04)" strokeWidth="0.5" />
          <line x1="515" y1="350" x2="580" y2="100" stroke="rgba(26,138,99,0.03)" strokeWidth="0.5" />
          <path d="M 60 300 Q 250 150 400 220 T 740 120" fill="none" stroke="rgba(79,41,126,0.05)" strokeWidth="1.5" strokeDasharray="6 4" />
          <path d="M 100 350 Q 300 250 500 300 T 780 200" fill="none" stroke="rgba(26,138,99,0.04)" strokeWidth="1" strokeDasharray="4 6" />
        </svg>
        <div className="biz__hero-inner">
          <div className="biz__hero-content">
            <span className="biz__tag">For Businesses</span>
            <div className="biz__hero-title">Enterprise Financial<br />Intelligence Platform</div>
            <p className="biz__hero-sub">Transform your financial operations with institutional-grade analytics, automated compliance, and real-time risk management — built for Indian businesses.</p>
            <div className="biz__hero-actions">
              <a href="#roi-calculator" className="biz__btn-primary">Calculate Your ROI <span>↓</span></a>
              <a href="/contact" className="biz__btn-outline">Schedule Demo</a>
            </div>
          </div>
          <div className="biz__hero-visual">
            <div className="biz__hero-card">
              <div className="biz__hero-card-row"><span className="biz__hc-label">Liquidity Score</span><span className="biz__hc-val biz__green">94/100</span></div>
              <div className="biz__hero-card-row"><span className="biz__hc-label">Receivables Health</span><span className="biz__hc-val">Strong</span></div>
              <div className="biz__hero-card-row"><span className="biz__hc-label">Operational Efficiency</span><span className="biz__hc-val" style={{color:'#A67C00'}}>78%</span></div>
              <div className="biz__hero-card-row"><span className="biz__hc-label">Runway Status</span><span className="biz__hc-val biz__green">Healthy</span></div>
              <div className="biz__hero-card-bar"><div style={{width:'72%'}}/></div>
              <div className="biz__hero-card-foot">Financial Health: <strong className="biz__green">Strong</strong></div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="biz__trust">
        <div className="biz__inner">
          <div className="biz__trust-grid">
            {[
              { val: '500+', label: 'Beta Trials' },
              { val: '3+', label: 'Enterprise Tools' },
              { val: '99.9%', label: 'System Uptime' },
              { val: '24/7', label: 'Support' },
            ].map((s, i) => (
              <div key={i} className="biz__trust-item">
                <div className="biz__trust-val">{s.val}</div>
                <div className="biz__trust-label">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SOLUTIONS */}
      <section className="biz__section biz__section--solutions" id="features">
        <div className="biz__inner">
          <div className="biz__section-header">
            <span className="biz__tag">Solutions</span>
            <div className="biz__section-title">Comprehensive Financial Solutions</div>
            <p className="biz__section-sub">Four pillars of enterprise-grade financial infrastructure, purpose-built for Indian regulatory requirements.</p>
          </div>
          <div className="biz__tabs">
            {Object.keys(tabs).map(t => (
              <button key={t} className={`biz__tab${activeTab === t ? ' biz__tab--on' : ''}`} onClick={() => setActiveTab(t)} type="button">
                {t.charAt(0).toUpperCase() + t.slice(1)}
              </button>
            ))}
          </div>
          <div className="biz__cards-grid">
            {tabs[activeTab].map((f, i) => (
              <div key={i} className="biz__card">
                <div className="biz__card-icon"><Icon type={f.icon} /></div>
                <div className="biz__card-title">{f.title}</div>
                <p className="biz__card-desc">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ROI CALCULATOR */}
      <ROICalculator />

      {/* PRODUCTS */}
      <section className="biz__section biz__section--products" id="products">
        <svg className="biz__section-art" viewBox="0 0 800 300" preserveAspectRatio="xMidYMid slice">
          <circle cx="680" cy="60" r="70" fill="none" stroke="rgba(79,41,126,0.04)" strokeWidth="1" />
          <circle cx="120" cy="240" r="50" fill="none" stroke="rgba(26,138,99,0.04)" strokeWidth="1" />
          <path d="M 50 80 Q 200 200 400 150 T 750 100" fill="none" stroke="rgba(79,41,126,0.04)" strokeWidth="1" strokeDasharray="5 5" />
        </svg>
        <div className="biz__inner">
          <div className="biz__section-header">
            <span className="biz__tag">Product Suite</span>
            <div className="biz__section-title">Built for Scale</div>
            <p className="biz__section-sub">Three integrated platforms that grow with your business — from startup to enterprise.</p>
          </div>
          <div className="biz__products-grid">
            {products.map((p, i) => (
              <div key={i} className="biz__product">
                <div className="biz__product-num">0{i + 1}</div>
                <div className="biz__product-title">{p.title}</div>
                <p className="biz__product-desc">{p.desc}</p>
                <div className="biz__product-features">
                  {p.features.map((f, j) => <div key={j} className="biz__product-feat">✓ {f}</div>)}
                </div>
                <a href="/contact" className="biz__product-cta">Learn More →</a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CLIENT CHALLENGES */}
      <section className="biz__section biz__section--challenges">
        <div className="biz__inner">
          <div className="biz__section-header">
            <span className="biz__tag">Client Voices</span>
            <div className="biz__section-title">We Understand Your Challenges</div>
          </div>
          <div className="biz__challenges">
            {challenges.map((c, i) => (
              <div key={i} className={`biz__challenge${activeSlide === i ? ' biz__challenge--on' : ''}`}>
                <div className="biz__challenge-quote">"{c.quote}"</div>
                <div className="biz__challenge-author">{c.author}</div>
                <div className="biz__challenge-role">{c.role}</div>
              </div>
            ))}
            <div className="biz__dots">
              {challenges.map((_, i) => (
                <button key={i} className={`biz__dot${activeSlide === i ? ' biz__dot--on' : ''}`} onClick={() => setActiveSlide(i)} type="button" />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="biz__section biz__section--cta">
        <div className="biz__inner">
          <div className="biz__cta">
            <svg className="biz__cta-art" viewBox="0 0 600 120" preserveAspectRatio="xMidYMid slice">
              <circle cx="70" cy="55" r="45" fill="none" stroke="rgba(79,41,126,0.06)" strokeWidth="1" />
              <circle cx="530" cy="45" r="35" fill="none" stroke="rgba(26,138,99,0.05)" strokeWidth="1" />
              <path d="M 50 100 Q 180 20 320 55 T 560 25" fill="none" stroke="rgba(79,41,126,0.05)" strokeWidth="1" strokeDasharray="5 4" />
            </svg>
            <div className="biz__cta-title">Ready to modernise your financial operations?</div>
            <p className="biz__muted" style={{ maxWidth: 500, margin: '0 auto 28px' }}>Join 500+ businesses that have transformed their financial workflows with SOMFIN's enterprise platform.</p>
            <div className="biz__cta-btns">
              <a href="/contact" className="biz__btn-primary">Schedule a Demo <span>→</span></a>
              <a href="#roi-calculator" className="biz__btn-outline">Calculate ROI</a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FinanceCore;